import { ChangeEvent, useState } from "react";
import SubmitSkeleton from "../SubmitSkeleton";
import { capitalizeFirstLetters } from "../../../utils/stringUtils";
import { useCaseContext } from "../../../context/CaseContext";
import { locations } from "../resources";
import DigitalCaseLinksInput from "../DigitalCaseLinksInput";

export const digitalLocations = ["social media", "online"] as const;
export type DigitalLocationType = (typeof digitalLocations)[number];

const IncidentLocationStage = ({
  setStep,
}: {
  setStep: (step: number) => void;
}) => {
  const [errorMessage, setErrorMessage] = useState("");

  const { caseType, incidentLocation, incidentDescription, updateCaseData } =
    useCaseContext();

  // const canProceed =
  //   incidentLocation &&
  //   (typeof incidentLocation === "string" ||
  //     (incidentLocation.custom && incidentLocation.customValue));

  const canProceed =
    incidentLocation &&
    // For non-digital locations, any valid selection is enough
    ((typeof incidentLocation === "string" &&
      !["online", "social media"].includes(incidentLocation)) ||
      // For custom locations, need a custom value
      (typeof incidentLocation === "object" &&
        incidentLocation.custom &&
        incidentLocation.customValue) ||
      // For digital locations, need at least one URL
      (typeof incidentLocation === "string" &&
        ["online", "social media"].includes(incidentLocation) &&
        incidentDescription.digitalcaseLinks &&
        incidentDescription.digitalcaseLinks.length > 0));

  const handleNext = () => {
    if (!incidentLocation) {
      setErrorMessage("Please select a location before proceeding.");
      return;
    }

    // Check if it's a digital location requiring URLs
    if (
      typeof incidentLocation === "string" &&
      ["online", "social media"].includes(incidentLocation)
    ) {
      // Check if URLs are provided
      if (
        !incidentDescription.digitalcaseLinks ||
        incidentDescription.digitalcaseLinks.length === 0
      ) {
        setErrorMessage(
          `Please add at least one URL for the ${incidentLocation} location.`
        );
        return;
      }
    }

    // If we reach here, everything is valid
    setErrorMessage("");
    setStep(4);
  };

  const handleChange = (choice: string) => {
    if (choice === "other") {
      // If "Other" is selected, we store a custom value in the context
      updateCaseData("incidentLocation", { custom: true, customValue: "" });
    } else {
      // Otherwise, store the selected predefined option
      updateCaseData("incidentLocation", choice);
    }
    setErrorMessage("");
  };

  const handleOtherLocationChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    updateCaseData("incidentLocation", { custom: true, customValue: value });
  };

  const handleAddLink = (link: string) => {
    const currentLinks = incidentDescription.digitalcaseLinks || [];
    updateCaseData("incidentDescription", {
      ...incidentDescription,
      digitalcaseLinks: [...currentLinks, link],
    });
  };

  const handleRemoveLink = (index: number) => {
    const currentLinks = incidentDescription.digitalcaseLinks || [];
    const newLinks = [...currentLinks];
    newLinks.splice(index, 1);
    updateCaseData("incidentDescription", {
      ...incidentDescription,
      digitalcaseLinks: newLinks,
    });
  };

  const isDigitalLocation =
    typeof incidentLocation === "string" &&
    (incidentLocation === "social media" || incidentLocation === "online");

  return (
    <SubmitSkeleton
      title="Where did this incident occur?"
      onPrev={() => setStep(2)}
      onNext={handleNext}
      canProceed={!!canProceed}
      progress={3 / 10}
    >
      <div className="flex flex-col gap-4">
        {errorMessage && (
          <p className="text-red-500 text-sm mb-1">{errorMessage}</p>
        )}

        <div className="flex-col w-full">
          {locations?.map((choice, i) => {
            // Skip locations that aren't relevant for digital abuse cases
            const digitalAbuseLocations = ["social media", "online"];

            if (
              caseType === "digital abuse" &&
              !digitalAbuseLocations.includes(choice)
            ) {
              return null;
            }

            const isSelected =
              typeof incidentLocation === "string"
                ? incidentLocation === choice
                : incidentLocation?.custom && choice === "other";

            return (
              <label
                key={i}
                className="flex items-center gap-3 pr-6 pl-[13px] cursor-pointer"
              >
                <input
                  type="radio"
                  name="subcase"
                  value={choice}
                  checked={isSelected}
                  onChange={() => handleChange(choice)}
                />
                <span className="font-[400] text-base leading-4 text-black py-4">
                  {capitalizeFirstLetters(choice)}
                </span>
              </label>
            );
          })}

          {incidentLocation &&
            typeof incidentLocation === "object" &&
            incidentLocation.custom && (
              <label className="flex flex-col gap-y-2">
                <span className="font-bold text-[14px] leading-[16.94px] text-black mt-2">
                  Other
                </span>
                <input
                  type="text"
                  value={incidentLocation.customValue}
                  onChange={handleOtherLocationChange}
                  placeholder="Please specify..."
                  className="border border-gray-3 py-2 px-3 rounded-md box-border outline-none focus:w-[99%] focus:ring-1 focus:ring-black focus:border-transparent "
                />
              </label>
            )}

          {isDigitalLocation && (
            <div className="mt-4">
              <DigitalCaseLinksInput
                links={incidentDescription.digitalcaseLinks || []}
                onAddLink={handleAddLink}
                onRemoveLink={handleRemoveLink}
              />
            </div>
          )}
        </div>
      </div>
    </SubmitSkeleton>
  );
};

export default IncidentLocationStage;
